// Counts a number up from `from` (or the previous value) to `value` in tabular numerals. The DOM text is always the
// final value (screen readers and tests read it); the moving digits are drawn by a pseudo-element.
import { useEffect, useRef } from 'react';
import { prefersReducedMotion } from './reduced';

const fmt = (n: number) => Math.round(n).toLocaleString('en-GB');
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3.2);

export function CountUp({ value, from, durationMs = 900, prefix = '', suffix = '', className = '' }: {
  value: number; from?: number; durationMs?: number; prefix?: string; suffix?: string; className?: string;
}) {
  const visual = useRef<HTMLSpanElement>(null);
  const shown = useRef<number>(from ?? value);

  useEffect(() => {
    const el = visual.current; if (!el) return;
    const start = shown.current; const end = value;
    const paint = (n: number) => { el.dataset.v = `${prefix}${fmt(n)}${suffix}`; };
    if (start === end || prefersReducedMotion() || typeof requestAnimationFrame === 'undefined') { shown.current = end; paint(end); return; }
    let raf = 0; const t0 = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / durationMs);
      const n = start + (end - start) * easeOut(t);
      shown.current = n; paint(n);
      if (t < 1) raf = requestAnimationFrame(step); else shown.current = end;
    };
    paint(start);
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); };
  }, [value, prefix, suffix, durationMs]);

  return (
    <span className={`numeral relative inline-block ${className}`}>
      <span className="sr-only">{prefix}{fmt(value)}{suffix}</span>
      <span ref={visual} aria-hidden="true" className="rd-count" data-v={`${prefix}${fmt(from ?? value)}${suffix}`} />
    </span>
  );
}
